import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import useManagerStore from '../../store/managerStore'
import { useAuthStore } from '../../store/authStore'
import { getReadyBranchTenantId } from '../tenantId'
import { createRealtimeWsClient } from './wsClient'
import { WsSessionContext } from './wsSessionHooks'

// Track B (realtime-inbox migration) FE1.1 — the one session-level WS
// connection, established once and kept alive for the whole app session.
// growchat-doc/realtime-inbox/spec-websocket-conversation-migration.md
//
// Mounted in src/components/layout/LangLayout.jsx, wrapping <Outlet/> — NOT
// inside AppShell.jsx/DesktopShell.jsx. Verified against the actual
// component tree (not assumed from the task's suggested location): every
// page (DashboardPage, InboxPage, ...) renders its own
// <ResponsiveShell>->AppShell/DesktopShell, so those two remount on every
// SPA navigation between pages. A WS session owned by either of them would
// reconnect (new ticket, new socket) on every route change instead of
// staying alive for the session, which is what FE1.1 requires. LangLayout
// wraps <Outlet/> once and only remounts on auth/lang-segment transitions
// (login/logout) — exactly when the WS session should also tear down.
//
// The consumer hooks (useWsSession/useWsEvent) live in wsSessionHooks.js, a
// plain-JS sibling module (deliberately NOT wsSessionContext.js/.jsx — this
// project's default filesystem is case-insensitive, and a name differing
// only by the first letter's case collided with this file) — keeping this
// .jsx file's only export a component satisfies
// react-refresh/only-export-components.
//
// Phase 2 cutover (growchat-doc/realtime-inbox/
// spec-websocket-cutover-remove-polling.md) — the WS_REALTIME_ENABLED
// GrowthBook flag gate (formerly FE1.10) has been removed entirely. All
// polling this WS session used to have as a fallback is gone too (see
// InboxPage.jsx, AppShell.jsx, DesktopShell.jsx), so a flag-off client would
// now have zero update mechanism instead of just falling back to polling —
// that turned what used to be a safe flag into a real kill-switch, which is
// exactly why it was removed rather than left "just don't touch it". The WS
// client below is now created/connected unconditionally as soon as
// token+readyTenantId are available; subscribeConversation/
// unsubscribeConversation remain safe no-ops before that (nothing to
// forward to yet).
export function WsSessionProvider({ children }) {
  const tenantId = useManagerStore((s) => s.tenantId)
  const branches = useAuthStore((s) => s.branches)
  const branchBootstrapStatus = useAuthStore((s) => s.branchBootstrapStatus)
  const token = useAuthStore((s) => s.token)
  const readyTenantId = useMemo(
    () => getReadyBranchTenantId(tenantId, branches, branchBootstrapStatus),
    [tenantId, branches, branchBootstrapStatus]
  )

  const [wsConnected, setWsConnected] = useState(false)
  const clientRef = useRef(null)
  const listenersRef = useRef(new Map())
  // What the app currently wants watched, owned here (not only in the client)
  // because a page's mount effect runs BEFORE the effect below creates the
  // client (child effects fire first, and the client also waits on token +
  // branch bootstrap). Forwarding to `clientRef.current?.` alone dropped those
  // early calls, so after a page load the socket opened but was never
  // subscribed to conversation_list / the open thread and live messages
  // never arrived. Re-applied to every new client before it connects; the
  // client re-sends them on each (re)open.
  const desiredThreadRef = useRef(null) // {contactId, channel} | null
  const desiredListRef = useRef(false)

  const dispatch = useCallback((event) => {
    const type = event?.type
    if (!type) return
    const handlers = listenersRef.current.get(type)
    if (!handlers || handlers.size === 0) return
    handlers.forEach((handler) => {
      try {
        handler(event)
      } catch (e) {
        Sentry.captureException(e, { tags: { module: 'ws_session', event_type: type } })
      }
    })
  }, [])

  useEffect(() => {
    // Not-ready: create nothing. If a previous render *did* have a live
    // client, React already ran that effect's cleanup below (which
    // disconnects and resets wsConnected to false) before this run, since
    // cleanup always fires ahead of the next effect body on a dependency
    // change — so there's nothing left to tear down or reset here.
    if (!token || !readyTenantId) return undefined

    const client = createRealtimeWsClient({
      tenantId: readyTenantId,
      onEvent: dispatch,
      onStatusChange: (status) => setWsConnected(status === 'connected'),
    })
    clientRef.current = client
    if (desiredThreadRef.current) {
      client.subscribeConversation(desiredThreadRef.current.contactId, desiredThreadRef.current.channel)
    }
    if (desiredListRef.current) client.subscribeConversationList()
    client.connect()

    return () => {
      client.disconnect()
      if (clientRef.current === client) clientRef.current = null
      setWsConnected(false)
    }
    // readyTenantId changing (e.g. branch switch) intentionally tears down
    // and re-establishes the session — the ws-ticket is tenant-scoped
    // server-side (see ws_ticket.py), so a stale client here would be
    // watching the wrong tenant's notifications keyspace.
  }, [token, readyTenantId, dispatch])

  const subscribeConversation = useCallback((contactId, channel) => {
    desiredThreadRef.current = contactId && channel ? { contactId, channel } : null
    clientRef.current?.subscribeConversation(contactId, channel)
  }, [])

  const unsubscribeConversation = useCallback(() => {
    desiredThreadRef.current = null
    clientRef.current?.unsubscribeConversation()
  }, [])

  // Track B2 (pagination-and-ws-tenant-wide, growchat-doc/realtime-inbox/
  // spec-inbox-conversation-list-pagination.md) — same forward-to-client
  // pattern as subscribeConversation/unsubscribeConversation above, for the
  // new tenant-wide `conversation_list` topic. Safe no-ops before the
  // client exists yet (token/readyTenantId not ready), same as those two.
  const subscribeConversationList = useCallback(() => {
    desiredListRef.current = true
    clientRef.current?.subscribeConversationList()
  }, [])

  const unsubscribeConversationList = useCallback(() => {
    desiredListRef.current = false
    clientRef.current?.unsubscribeConversationList()
  }, [])

  const subscribe = useCallback((type, handler) => {
    if (!listenersRef.current.has(type)) listenersRef.current.set(type, new Set())
    const set = listenersRef.current.get(type)
    set.add(handler)
    return () => {
      set.delete(handler)
    }
  }, [])

  const value = useMemo(
    () => ({
      wsConnected,
      subscribeConversation,
      unsubscribeConversation,
      subscribeConversationList,
      unsubscribeConversationList,
      subscribe,
    }),
    [
      wsConnected,
      subscribeConversation,
      unsubscribeConversation,
      subscribeConversationList,
      unsubscribeConversationList,
      subscribe,
    ]
  )

  return <WsSessionContext.Provider value={value}>{children}</WsSessionContext.Provider>
}
